<template>
  <div class="callback-container">
    <div class="callback-card">
      <!-- Success State -->
      <div v-if="status === 'complete'" class="success-state">
        <div class="icon-wrapper success">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <h1 class="title success">Successfully Connected!</h1>
        <p class="message">Your WhatsApp Business Account has been connected successfully.</p>
        <p class="countdown">This window will close automatically in {{ countdown }} seconds...</p>
        <button @click="closeWindow" class="close-button">Close Now</button>
      </div>

      <!-- Partial Success State (needs manual input) -->
      <div v-else-if="status === 'success'" class="partial-state">
        <div class="icon-wrapper warning">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
            <path d="M12 9v4"/>
            <path d="M12 17h.01"/>
          </svg>
        </div>
        <h1 class="title warning">Almost Done!</h1>
        <p class="message">Authentication successful, but we need a bit more information.</p>
        <p class="instruction">Please close this window and complete the setup on the previous page.</p>
        <button @click="closeWindow" class="close-button warning">Go Back to Settings</button>
      </div>

      <!-- Error State -->
      <div v-else-if="status === 'error'" class="error-state">
        <div class="icon-wrapper error">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h1 class="title error">Connection Failed</h1>
        <p class="message">{{ errorMessage || 'Unable to connect your WhatsApp Business Account.' }}</p>
        <button @click="closeWindow" class="close-button error">Close & Try Again</button>
      </div>

      <!-- Loading State -->
      <div v-else class="loading-state">
        <div class="spinner-wrapper">
          <div class="spinner"></div>
        </div>
        <h1 class="title">Processing...</h1>
        <p class="message">Please wait while we set up your WhatsApp Business Account.</p>
      </div>
    </div>

    <div class="footer-note">
      <p>If this window doesn't close automatically, you can close it manually.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EmbeddedSignupCallbackView',
  data () {
    return {
      status: null, // 'complete', 'success', 'error', null (loading)
      errorMessage: '',
      countdown: 5
    }
  },
  mounted () {
    this.checkStatus()
  },
  methods: {
    checkStatus () {
      // Get status from URL parameters
      const urlParams = new URLSearchParams(window.location.search)
      const embeddedSignupStatus = urlParams.get('embedded_signup')
      const error = urlParams.get('error')

      if (error) {
        this.status = 'error'
        this.errorMessage = decodeURIComponent(error)
      } else if (embeddedSignupStatus === 'complete') {
        this.status = 'complete'
        this.startCountdown()
      } else if (embeddedSignupStatus === 'success') {
        this.status = 'success'
      } else {
        // Unknown status
        this.status = 'error'
        this.errorMessage = 'Invalid callback status'
      }

      // Notify parent window if it exists
      if (window.opener) {
        try {
          window.opener.postMessage({
            type: 'whatsapp-embedded-signup',
            status: this.status,
            error: this.errorMessage
          }, window.location.origin)
        } catch (e) {
          console.error('Could not notify parent window:', e)
        }
      }
    },

    startCountdown () {
      const interval = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(interval)
          this.closeWindow()
        }
      }, 1000)
    },

    closeWindow () {
      // Try to close the window
      window.close()

      // If window.close() doesn't work (some browsers prevent it),
      // show a message after a short delay
      setTimeout(() => {
        if (!window.closed) {
          alert('Please close this window manually and return to the settings page.')
        }
      }, 500)
    }
  }
}
</script>

<style scoped>
.callback-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.callback-card {
  background: white;
  border-radius: 20px;
  padding: 60px 40px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.icon-wrapper {
  margin: 0 auto 30px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-wrapper.success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
}

.icon-wrapper.warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #d97706;
}

.icon-wrapper.error {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
}

.title {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 16px;
  color: #1f2937;
}

.title.success {
  color: #059669;
}

.title.warning {
  color: #d97706;
}

.title.error {
  color: #dc2626;
}

.message {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 12px;
  line-height: 1.6;
}

.countdown {
  font-size: 14px;
  color: #9ca3af;
  margin: 20px 0;
  font-weight: 500;
}

.instruction {
  font-size: 15px;
  color: #4b5563;
  margin: 20px 0;
  font-weight: 500;
}

.close-button {
  margin-top: 30px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.close-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(5, 150, 105, 0.4);
}

.close-button.warning {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3);
}

.close-button.warning:hover {
  box-shadow: 0 8px 20px rgba(217, 119, 6, 0.4);
}

.close-button.error {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.close-button.error:hover {
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.4);
}

.footer-note {
  margin-top: 30px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  text-align: center;
}

.footer-note p {
  margin: 0;
}

/* Loading State */
.loading-state {
  padding: 20px 0;
}

.spinner-wrapper {
  margin: 0 auto 30px;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 80px;
  height: 80px;
  border: 6px solid #e5e7eb;
  border-top: 6px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 600px) {
  .callback-card {
    padding: 40px 24px;
  }

  .title {
    font-size: 26px;
  }

  .icon-wrapper {
    width: 100px;
    height: 100px;
  }

  .icon-wrapper svg {
    width: 60px;
    height: 60px;
  }
}
</style>
